import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

interface UserPreferences {
  language: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  province: string;
}

const UserSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [preferences, setPreferences] = useState<UserPreferences>({
    language: i18n.language || 'en',
    dateFormat: localStorage.getItem('dateFormat') || 'MM/DD/YYYY',
    timeFormat: localStorage.getItem('timeFormat') || '12h',
    timezone: localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone,
    province: localStorage.getItem('province') || 'QC',
  });
  const [saved, setSaved] = useState(false);

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ];

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/01/2025)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (01/12/2025)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-01)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY (01 Dec 2025)' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 01, 2025)' },
  ];

  const timeFormats = [
    { value: '12h', label: '12-hour (2:30 PM)' },
    { value: '24h', label: '24-hour (14:30)' },
  ];

  const provinces = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
  ];

  // Common timezones
  const timezones = [
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Montreal', label: 'Eastern Time (Montreal)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
    { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)' },
    { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)' },
    { value: 'America/Halifax', label: 'Atlantic Time (Halifax)' },
    { value: 'America/St_Johns', label: 'Newfoundland Time (St. John\'s)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'Europe/London', label: 'GMT (London)' },
    { value: 'Europe/Paris', label: 'CET (Paris)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    // Load preferences from localStorage on mount
    const savedLang = localStorage.getItem('language');
    if (savedLang) {
      setPreferences(prev => ({ ...prev, language: savedLang }));
    }
  }, []);

  const handleChange = (key: keyof UserPreferences, value: string) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem('language', preferences.language);
    localStorage.setItem('dateFormat', preferences.dateFormat);
    localStorage.setItem('timeFormat', preferences.timeFormat);
    localStorage.setItem('timezone', preferences.timezone);
    localStorage.setItem('province', preferences.province);

    // Update i18n language
    i18n.changeLanguage(preferences.language);

    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const selectClasses = isDark
    ? 'w-full px-3 py-2 border border-slate-600 bg-slate-700 text-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500'
    : 'w-full px-3 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('settings.preferences')}</h2>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('settings.preferencesDescription')}</p>
      </div>

      {saved && (
        <div className={`p-3 rounded-lg flex items-center ${isDark ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' : 'bg-green-50 border border-green-200 text-green-700'}`}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {t('settings.saved')}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Language */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('settings.language')}
          </label>
          <select
            value={preferences.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className={selectClasses}
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{t('settings.languageHelp')}</p>
        </div>

        {/* Date Format */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('settings.dateFormat')}
          </label>
          <select
            value={preferences.dateFormat}
            onChange={(e) => handleChange('dateFormat', e.target.value)}
            className={selectClasses}
          >
            {dateFormats.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{t('settings.dateFormatHelp')}</p>
        </div>

        {/* Time Format */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('settings.timeFormat')}
          </label>
          <select
            value={preferences.timeFormat}
            onChange={(e) => handleChange('timeFormat', e.target.value)}
            className={selectClasses}
          >
            {timeFormats.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{t('settings.timeFormatHelp')}</p>
        </div>

        {/* Timezone */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('settings.timezone')}
          </label>
          <select
            value={preferences.timezone}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className={selectClasses}
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{t('settings.timezoneHelp')}</p>
        </div>

        {/* Province */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            {t('settings.province')}
          </label>
          <select
            value={preferences.province}
            onChange={(e) => handleChange('province', e.target.value)}
            className={selectClasses}
          >
            {provinces.map((prov) => (
              <option key={prov.value} value={prov.value}>
                {prov.label}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{t('settings.provinceHelp')}</p>
        </div>
      </div>

      <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all"
        >
          {t('settings.savePreferences')}
        </button>
      </div>
    </div>
  );
};

export default UserSettings;
